import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { TABLES } from '../../lib/constants';
import { useAuth } from '../../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';

const { FiShield, FiUser, FiPlus, FiTrash2, FiUsers, FiEdit3, FiX, FiCheck, FiSave } = FiIcons;

const AdminDashboard = () => {
  const { user, impersonate } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const [data, setData] = useState({ masters: [], pilots: [], globalItems: [] });
  const [loading, setLoading] = useState(false);

  // User Management State
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userFormData, setUserFormData] = useState({
    username: '',
    nickname: '',
    role: 'master',
    master_id: ''
  });

  useEffect(() => {
    loadAllData();
  }, [activeTab]);

  const loadAllData = async () => {
    setLoading(true);
    const { data: users } = await supabase.from(TABLES.USERS).select('*');
    if (users) {
      setData({
        masters: users.filter(u => u.role === 'master'),
        pilots: users.filter(u => u.role === 'pilot'),
        globalItems: []
      });
    }
    setLoading(false);
  };

  const openUserModal = (userToEdit = null) => {
    if (userToEdit) {
      setEditingUser(userToEdit);
      setUserFormData({
        username: userToEdit.username,
        nickname: userToEdit.nickname || '',
        role: userToEdit.role,
        master_id: userToEdit.master_id || ''
      });
    } else {
      setEditingUser(null);
      setUserFormData({
        username: '',
        nickname: '',
        role: 'master',
        master_id: ''
      });
    }
    setIsUserModalOpen(true);
  };

  const handleSaveUser = async () => {
    if (!userFormData.username.trim()) return;
    setLoading(true);
    try {
      const payload = {
        username: userFormData.username.trim(),
        nickname: userFormData.nickname.trim(),
        role: userFormData.role,
        master_id: userFormData.role === 'pilot' ? (userFormData.master_id || null) : null
      };

      let error;
      if (editingUser) {
        const { error: err } = await supabase.from(TABLES.USERS).update(payload).eq('id', editingUser.id);
        error = err;
      } else {
        const { error: err } = await supabase.from(TABLES.USERS).insert([payload]);
        error = err;
      }

      if (error) throw error;
      
      alert(editingUser ? 'Account updated successfully!' : `New ${userFormData.role} created successfully!`);
      setIsUserModalOpen(false);
      loadAllData();
    } catch (err) {
      alert(err.message.includes('unique') ? 'Username already exists' : err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (id) => {
    if (!confirm('Are you sure you want to delete this account? All associated data will be lost.')) return;
    const { error } = await supabase.from(TABLES.USERS).delete().eq('id', id);
    if (!error) {
      alert('Account deleted successfully.');
      loadAllData();
    } else {
      alert(error.message);
    }
  };

  return (
    <div className="space-y-12 pb-20">
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h2 className="text-5xl font-black text-gray-900 tracking-tight">System Control</h2>
          <p className="text-gray-400 font-bold text-lg mt-2">Global Oversight & Data Management</p>
        </div>
        <div className="flex bg-gray-200/50 p-1.5 rounded-[1.5rem] backdrop-blur-md border border-white">
          <button onClick={() => setActiveTab('users')} className={`px-6 py-3 rounded-2xl font-black text-xs md:text-sm transition-all whitespace-nowrap ${activeTab === 'users' ? 'bg-white shadow-xl text-blue-600' : 'text-gray-500 hover:text-gray-900'}`}>Accounts</button>
        </div>
      </header>

      {activeTab === 'users' && (
        <div className="space-y-8 animate-in fade-in duration-500">
          <div className="flex justify-end">
            <button onClick={() => openUserModal()} className="apple-button-primary flex items-center justify-center gap-2 shadow-xl !py-4 md:!py-5 !px-8 text-sm md:text-base">
              <FiPlus className="text-xl" /> Create New Account
            </button>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bento-card">
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-xl font-black flex items-center gap-3"><FiShield className="text-blue-600"/> Masters</h3>
                <span className="px-3 py-1 bg-blue-50 text-blue-600 text-[10px] font-black rounded-full uppercase">{data.masters.length} Total</span>
              </div>
              <div className="space-y-3">
                {data.masters.map(m => (
                  <div key={m.id} className="flex items-center justify-between p-5 bg-gray-50 rounded-[2rem] border border-transparent hover:border-blue-100 transition-all group">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm text-gray-400 group-hover:text-blue-600 transition-all"><FiUser /></div>
                      <div>
                        <span className="font-black text-gray-700 block">{m.nickname || m.username}</span>
                        {m.nickname && <span className="text-[9px] font-bold text-gray-400 uppercase">@{m.username}</span>}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => openUserModal(m)} className="p-2.5 text-blue-600 bg-white rounded-xl shadow-sm hover:bg-blue-600 hover:text-white transition-all"><FiEdit3 /></button>
                      <button onClick={() => impersonate(m)} className="text-[10px] font-black uppercase text-blue-600 bg-white px-4 py-2 rounded-xl shadow-sm border border-blue-50 hover:bg-blue-50">Audit</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Pilots List */}
            <div className="bento-card">
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-xl font-black flex items-center gap-3"><FiUsers className="text-indigo-600"/> Pilots</h3>
                <span className="px-3 py-1 bg-indigo-50 text-indigo-600 text-[10px] font-black rounded-full uppercase">{data.pilots.length} Total</span>
              </div>
              <div className="space-y-3">
                {data.pilots.map(p => (
                  <div key={p.id} className="flex items-center justify-between p-5 bg-gray-50 rounded-[2rem] border border-transparent hover:border-indigo-100 transition-all group">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm text-gray-400 group-hover:text-indigo-600 transition-all"><FiUser /></div>
                      <div>
                        <span className="font-black text-gray-700 block">{p.nickname || p.username}</span>
                        <span className="text-[9px] font-bold text-gray-400 uppercase">Pilot</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => openUserModal(p)} className="p-2.5 text-blue-600 bg-white rounded-xl shadow-sm hover:bg-indigo-600 hover:text-white transition-all"><FiEdit3 /></button>
                      <button onClick={() => impersonate(p)} className="text-[10px] font-black uppercase text-indigo-600 bg-white px-4 py-2 rounded-xl shadow-sm border border-indigo-50 hover:bg-indigo-50">Logs</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Management Modal */}
      <AnimatePresence>
        {isUserModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-md">
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="w-full max-w-2xl bg-white rounded-[2.5rem] p-8 md:p-12 shadow-2xl overflow-y-auto max-h-[90vh]">
              <div className="flex justify-between items-center mb-10">
                <h3 className="text-2xl font-black">{editingUser ? 'Edit Account' : 'Create Account'}</h3>
                <button onClick={() => setIsUserModalOpen(false)} className="p-2 bg-gray-100 rounded-full"><FiX /></button>
              </div>
              <div className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-2">Username (Unique ID)</label>
                    <input className="apple-input" value={userFormData.username} onChange={e => setUserFormData({ ...userFormData, username: e.target.value })} placeholder="e.g., joe_pilot" />
                  </div>
                  <div className="space-y-3">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-2">Display Nickname</label>
                    <input className="apple-input" value={userFormData.nickname} onChange={e => setUserFormData({ ...userFormData, nickname: e.target.value })} placeholder="e.g., Joe the Farmer" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-2">Account Role</label>
                    <select className="apple-input" value={userFormData.role} onChange={e => setUserFormData({ ...userFormData, role: e.target.value })}>
                      <option value="master">Guild Master</option>
                      <option value="pilot">Farmer Pilot</option>
                    </select>
                  </div>
                  {userFormData.role === 'pilot' && (
                    <div className="space-y-3">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-2">Assign to Master</label>
                      <select className="apple-input" value={userFormData.master_id} onChange={e => setUserFormData({ ...userFormData, master_id: e.target.value })}>
                        <option value="">-- Unassigned --</option>
                        {data.masters.map(m => <option key={m.id} value={m.id}>{m.nickname || m.username}</option>)}
                      </select>
                    </div>
                  )}
                </div>
                <div className="pt-10 flex flex-col md:flex-row gap-4">
                  {editingUser && (
                    <button onClick={() => handleDeleteUser(editingUser.id)} className="apple-button-secondary !text-red-500 !bg-red-50 order-2 md:order-1">Delete Account</button>
                  )}
                  <button onClick={handleSaveUser} disabled={loading} className="apple-button-primary flex-1 flex items-center justify-center gap-2 order-1 md:order-2">
                    {loading ? '...' : (editingUser ? 'Save Updates' : 'Create Account')}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminDashboard;